import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHero, Section, Button, PROVINCES, SERVICES } from "@/components/UI";

export default function RegistryApply() {
  const empty = { name: "", credentials: "", clinic_name: "", city: "", province: "", email: "", phone: "", website: "", services: [], bio: "", photo_url: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.province) return toast.error("Please select a province.");
    if (form.services.length === 0) return toast.error("Please select at least one service.");
    setBusy(true);
    try {
      await api.post("/practitioners", form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit application.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <>
        <PageHero overline="Expression of Interest" title="Thank you for your interest" testId="apply-done-hero"
          subtitle="We've received your details. As a new organization, CCPM is building its national standard and certification framework — and we'll keep you informed as they develop." />
        <Section className="py-20 lg:py-28">
          <div className="max-w-2xl border border-ccpm-border bg-white p-10 lg:p-14">
            <CheckCircle2 size={44} strokeWidth={1.3} className="text-ccpm-olive" />
            <h2 className="font-serif text-3xl text-ccpm-charcoal mt-6">What happens next</h2>
            <ol className="mt-6 space-y-4 text-ccpm-taupe font-light leading-relaxed list-decimal pl-5">
              <li>Your details are recorded so we can reach you as certification becomes available.</li>
              <li>We'll share updates on our standards, education, and how to become certified.</li>
              <li>When certification launches and you are verified, your profile can be published in the public registry.</li>
            </ol>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button to="/registry" variant="primary" data-testid="apply-back-registry">Back to the registry</Button>
              <Button to="/" variant="outline">Return home</Button>
            </div>
          </div>
        </Section>
      </>
    );
  }

  return (
    <>
      <PageHero overline="Expression of Interest" testId="apply-hero"
        title="Register your interest"
        subtitle="CCPM is being established and certification is not yet open. If you are a practitioner interested in future certification and inclusion in the national registry, share your details and we'll keep you informed." />

      <Section className="py-16 lg:py-24">
        <Link to="/registry" className="inline-flex items-center gap-2 text-sm text-ccpm-olive hover:underline mb-8"><ArrowLeft size={16} /> Registry</Link>
        <form onSubmit={submit} className="max-w-3xl bg-white border border-ccpm-border p-8 lg:p-12" data-testid="apply-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Full name *"><input required data-testid="apply-name" value={form.name} onChange={set("name")} className="capp-input" /></Field>
            <Field label="Credentials"><input placeholder="e.g. RN, CCPM-Certified" data-testid="apply-credentials" value={form.credentials} onChange={set("credentials")} className="capp-input" /></Field>
            <Field label="Clinic / practice name"><input data-testid="apply-clinic" value={form.clinic_name} onChange={set("clinic_name")} className="capp-input" /></Field>
            <Field label="City *"><input required data-testid="apply-city" value={form.city} onChange={set("city")} className="capp-input" /></Field>
            <Field label="Province *">
              <select required data-testid="apply-province" value={form.province} onChange={set("province")} className="capp-input">
                <option value="">Select a province</option>
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Email *"><input required type="email" data-testid="apply-email" value={form.email} onChange={set("email")} className="capp-input" /></Field>
            <Field label="Phone"><input data-testid="apply-phone" value={form.phone} onChange={set("phone")} className="capp-input" /></Field>
            <Field label="Website"><input placeholder="https://" data-testid="apply-website" value={form.website} onChange={set("website")} className="capp-input" /></Field>
          </div>

          <div className="mt-6"><Field label="Photo URL (optional)"><input placeholder="Link to a professional headshot" data-testid="apply-photo" value={form.photo_url} onChange={set("photo_url")} className="capp-input" /></Field></div>

          <div className="mt-8">
            <span className="overline text-ccpm-taupe block mb-3">Restorative services offered *</span>
            <div className="flex flex-wrap gap-3" data-testid="apply-services">
              {SERVICES.map((s) => {
                const active = form.services.includes(s);
                return (
                  <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`apply-service-${s.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-4 py-2 text-sm border transition-colors ${active ? "bg-ccpm-olive text-ccpm-ivory border-ccpm-olive" : "bg-ccpm-ivory text-ccpm-taupe border-ccpm-border hover:border-ccpm-olive"}`}>{s}</button>
                );
              })}
            </div>
          </div>

          <div className="mt-8"><Field label="About your practice"><textarea rows={5} data-testid="apply-bio" value={form.bio} onChange={set("bio")} className="capp-input resize-none" placeholder="Briefly describe your restorative work, training, and approach." /></Field></div>

          <Button type="submit" disabled={busy} className="mt-8 w-full sm:w-auto" data-testid="apply-submit">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Register my interest"}
          </Button>
        </form>
      </Section>
      <style>{`.capp-input{width:100%;padding:0.75rem 0.9rem;background:#FDFBF7;border:1px solid #E6DFD9;font-size:0.9rem;color:#2B2B2B;outline:none;transition:border-color .2s}.capp-input:focus{border-color:#6B705C}`}</style>
    </>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="overline text-ccpm-taupe block mb-2">{label}</span>{children}</label>
);
